import { useParams, Link } from 'react-router-dom';
import { categories, articles } from '@/mocks/travelData';
import Breadcrumb from '@/components/base/Breadcrumb';

const categoryImageUrls: Record<string, string> = {
  'tropical-islands': 'https://readdy.ai/api/search-image?query=Tropical%20island%20paradise%20with%20crystal%20clear%20turquoise%20water%20white%20sandy%20beach%20palm%20trees%20luxury%20resort%20aerial%20view%20soft%20warm%20sunlight&width=1200&height=500&seq=cat-tropical-banner&orientation=landscape',
  'ancient-capitals': 'https://readdy.ai/api/search-image?query=Ancient%20Asian%20temple%20complex%20with%20traditional%20architecture%20golden%20roof%20intricate%20carvings%20misty%20morning%20cultural%20heritage%20serene%20atmosphere&width=1200&height=500&seq=cat-ancient-banner&orientation=landscape',
  'mountain-secrets': 'https://readdy.ai/api/search-image?query=Majestic%20mountain%20range%20with%20terraced%20rice%20fields%20misty%20valleys%20traditional%20villages%20golden%20sunrise%20dramatic%20clouds%20lush%20green%20landscape&width=1200&height=500&seq=cat-mountain-banner&orientation=landscape',
  'urban-romance': 'https://readdy.ai/api/search-image?query=Modern%20Asian%20city%20skyline%20at%20night%20with%20neon%20lights%20bustling%20street%20food%20market%20vibrant%20energy%20contemporary%20architecture%20warm%20glowing%20lanterns&width=1200&height=500&seq=cat-urban-banner&orientation=landscape',
  'wellness-retreat': 'https://readdy.ai/api/search-image?query=Luxury%20spa%20resort%20with%20natural%20hot%20springs%20surrounded%20by%20bamboo%20forest%20zen%20garden%20calm%20meditation%20soft%20steam%20rising%20traditional%20Asian%20architecture&width=1200&height=500&seq=cat-wellness-banner&orientation=landscape',
  'culinary-journey': 'https://readdy.ai/api/search-image?query=Vibrant%20Asian%20night%20food%20market%20with%20steaming%20street%20food%20stalls%20colorful%20lanterns%20fresh%20ingredients%20aromatic%20spices%20local%20chefs%20cooking%20warm%20lighting&width=1200&height=500&seq=cat-culinary-banner&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const category = categories.find((c) => c.slug === slug);
  const filteredArticles = articles.filter((a) => a.category === slug);
  const bannerUrl = slug ? categoryImageUrls[slug] : '';

  if (!category) {
    return (
      <main className="flex-1 pt-24 px-4">
        <div className="max-w-6xl mx-auto text-center py-20">
          <h1 className="font-heading text-2xl font-semibold text-foreground-900 mb-4">分类未找到</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-1">
      <section className="relative w-full h-64 md:h-80 overflow-hidden">
        <img
          src={bannerUrl}
          alt={category.name}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-white mb-2">
            {category.name}
          </h1>
          <p className="text-sm md:text-base text-white/80 max-w-lg">{category.description}</p>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-6 md:py-8">
        <div className="max-w-6xl mx-auto">
          <Breadcrumb items={[
            { label: '首页', to: '/' },
            { label: category.name },
          ]} />

          <div className="mt-6">
            <div className="flex items-center gap-2 mb-6 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-3 py-1.5 text-sm rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-full h-44 overflow-hidden">
                      <img
                        src={article.coverUrl}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <span className="text-xs text-foreground-500">{article.readTime}</span>
                        <span className="text-xs text-foreground-500">{article.date}</span>
                      </div>
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                        {article.title}
                      </h3>
                      <p className="text-xs text-foreground-600 line-clamp-2 leading-relaxed">
                        {article.excerpt}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": `${category.name} - 亚洲天堂`,
          "description": category.description,
          "url": `https://rstuwlw.cn/category/${category.slug}`,
          "hasPart": filteredArticles.map((a) => ({
            "@type": "Article",
            "headline": a.title,
            "url": `https://rstuwlw.cn/article/${a.slug}`,
            "author": { "@type": "Person", "name": a.author },
            "datePublished": a.date,
          })),
        })
      }} />
    </main>
  );
}